<template>
  <div 
    class="step__footer"
    :class="classes"
  >
    <template v-if="type === 'transactional'">
      <basic-button
        :data-track-as="$props.primaryTrackAs"
        :data-track-text="transactionalPrimaryLabel"
        :label="transactionalPrimaryLabel"
        @click="$emit('click-primary')"
      />
      <basic-button
        hierarchy="secondary"
        :data-track-as="$props.secondaryTrackAs"
        :data-track-text="transactionalSecondaryLabel"
        :label="transactionalSecondaryLabel"
        @click="$emit('click-secondary')"
      />
    </template>

    <template v-else-if="type === 'acknowledgement'">
      <basic-button
        :label="acknowledgementLabel"
        :data-track-as="$props.primaryTrackAs"
        :data-track-text="acknowledgementLabel"
        @click="$emit('click-primary')"
      />
    </template>

    <template v-else-if="type === 'progress'">
      <div class="nested">
        <basic-button
          v-if="$props.primaryVisible"
          :data-track-as="$props.primaryTrackAs"
          :data-track-text="progressNextLabel"
          :label="progressNextLabel"
          @click="$emit('click-primary')"
        />
        <basic-button
          v-if="$props.secondaryVisible"
          hierarchy="secondary"
          :data-track-as="$props.secondaryTrackAs"
          :data-track-text="progressBackLabel"
          :label="progressBackLabel"
          @click="$emit('click-secondary')"
        />
      </div>

      <basic-button
        hierarchy="tertiary"
        :label="progressCancelLabel"
        @click="$emit('click-tertiary')"
      />
    </template>

    <template v-else-if="type === 'passive'">
      <basic-button
        hierarchy="primary"
        :label="$t('form.close')"
        @click="modalOptions.modalRef.value.requestClose()"
      />
    </template>

    <template v-else>
      <slot />
    </template>
  </div>
</template>

<script setup>
  import { computed, inject } from 'vue'

  import useI18n from '@shared/hooks/useI18n'

  import BasicButton from '@shared/components/Basic/Button'

  // HOOKS
  const { t } = useI18n()

  // INIT
  defineEmits(['click-primary', 'click-secondary', 'click-tertiary'])
  const props = defineProps({
    alignement: {
      type: String,
      required: true
    },

    primaryLabel: {
      type: String,
      default: null,
    },

    primaryTrackAs: {
      type: String,
      default: null,
    },

    primaryVisible: {
      type: Boolean,
      default: true,
    },

    secondaryLabel: {
      type: String,
      default: null,
    },

    secondaryTrackAs: {
      type: String,
      default: null,
    },

    secondaryVisible: {
      type: Boolean,
      default: true,
    },

    tertiaryLabel: {
      type: String,
      default: null,
    },
  })

  // DATA
  const modalOptions = inject('modalOptions')

  // COMPUTED
  const classes = computed(() => {
    return {
      [`step__footer--alignement-${props.alignement}`]: true,
      [`step__footer--type-${type.value}`]: true
    }
  })

  const type = computed(() => {
    return modalOptions.props.type
  })

  const acknowledgementLabel = computed(() => {
    return modalOptions.props.primaryLabel || 'Accept' // TODO: CONTENT
  })

  const progressBackLabel = computed(() => {
    return modalOptions.props.secondaryLabel || t('form.back')
  })

  const progressCancelLabel = computed(() => {
    return modalOptions.props.tertiaryLabel || t('form.cancel')
  })

  const progressNextLabel = computed(() => {
    return modalOptions.props.primaryLabel || t('form.next')
  })

  const transactionalPrimaryLabel = computed(() => {
    return modalOptions.props.primaryLabel || t('form.save')
  })

  const transactionalSecondaryLabel = computed(() => {
    return modalOptions.props.secondaryLabel || t('form.cancel')
  })
</script>

<style scoped>
  .step__footer {
    display: flex;
    flex-direction: column;
    gap: var(--dotcom-responsive-spacing-res-xxs);
    width: 100%;
    max-width: 1544px;
    margin: 0 auto;

    .nested {
      display: flex;
      flex-direction: column;
      gap: var(--dotcom-responsive-spacing-res-xxs);
    }
  }
</style>

<style scoped>
  @media (--v-medium) {
    .step__footer {

      &.step__footer--alignement-left {
        flex-direction: row;
      }

      &.step__footer--alignement-center {
        flex-direction: row;
        justify-content: center;
      }

      &.step__footer--alignement-right {
        flex-direction: row-reverse;

        &.step__footer--type-progress {
          justify-content: space-between;

          .nested {
            flex-direction: row-reverse;
          }
        }
      }

      &.step__footer--alignement-spaced {
        flex-direction: row-reverse;
        justify-content: space-between;
      }
    }
  }
</style>